import { calculateBoxWidth } from "../../pages/dashboard/route-planner/helpers/timetable";
import { RouteStatus } from "../../pages/dashboard/route-planner/helpers/route-status.enum";

// Minimum width for boxes with 0 service time - enough to show the icon/sequence
export const ZERO_SERVICE_TIME_MIN_WIDTH = 20;

export const getAddressBoxStyleDetails = (
  duration: number,
  locationSequence: number,
  hasDynamicWidth: boolean,
  status: string,
  zoomLevel = 1.0,
  serviceTime?: number,
  currentRouteStatus?: RouteStatus,
) => {
  // Special case: orders with 0 service time should use a minimum visible width.
  // Layout flow compensation is handled in the address component via negative margin.
  const isZeroServiceTimeOrder = serviceTime === 0;

  // In live phase, use precise (non-rounded) widths for accurate timeline representation
  const isLivePhase = currentRouteStatus === RouteStatus.Live;

  // Calculate box width proportionally based on service time or duration
  // No minimum widths - boxes should fit proportionally within the timeline
  const boxWidth = isZeroServiceTimeOrder
    ? ZERO_SERVICE_TIME_MIN_WIDTH // Fixed minimum width for 0 service time orders
    : hasDynamicWidth && status !== "skipped"
      ? calculateBoxWidth(
          serviceTime && serviceTime > 0 ? serviceTime : duration,
          undefined, // Use default timelineItemWidth
          zoomLevel,
          isLivePhase, // Use precise width in live phase
        )
      : status === "skipped"
        ? 11 // Skipped addresses always use fixed 11px width
        : calculateBoxWidth(duration, undefined, zoomLevel, isLivePhase); // Use proportional width for non-dynamic cases too

  const locationSequenceString = locationSequence.toString();

  // Use absolute minimum widths that work well visually, regardless of zoom
  // These are the actual pixel widths needed for readable text at any zoom level
  const baseMinWidthForSingleDigit = 12; // Minimum pixels for single digit (e.g., "1", "5")
  const baseMinWidthForDoubleDigit = 18; // Minimum pixels for double digit (e.g., "10", "25")

  const absoluteMinWidth =
    locationSequenceString.length === 1
      ? baseMinWidthForSingleDigit
      : baseMinWidthForDoubleDigit;

  // Hide sequence numbers when zoom is too small for text readability
  // At 60% zoom and below (0.6x), text becomes too cramped to read clearly
  // This threshold ensures good UX - users can still see the boxes but without cluttered numbers
  const isZoomTooSmallForText = zoomLevel < 0.6;

  const shouldShowLocationSequence =
    status !== "skipped" &&
    boxWidth >= absoluteMinWidth &&
    !isZoomTooSmallForText;

  return {
    shouldShowLocationSequence,
    boxWidth,
  };
};

// Calculate darkened color for hover states using more compatible approach
export const getDarkenedColor = (baseColor: string) => {
  // Convert hex to RGB and darken by 30%
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  const rgb = hexToRgb(baseColor || "#cccccc");
  if (rgb) {
    // Darken by reducing each RGB component by 30%
    const factor = 0.7;
    const darkenedR = Math.floor(rgb.r * factor);
    const darkenedG = Math.floor(rgb.g * factor);
    const darkenedB = Math.floor(rgb.b * factor);
    return `rgb(${darkenedR}, ${darkenedG}, ${darkenedB})`;
  }

  // Fallback for non-hex colors
  return `rgba(0, 0, 0, 0.3)`; // Semi-transparent black overlay effect
};
